import { Controller, type UseFormReturn } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import { startOfToday } from 'date-fns'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DatePickerCell } from '@/components/common/DatePickerCell'
import type { CommitmentLetterPrepareResponse } from '@/api/commitmentLetters'
import type { CommitmentLetterFormInput } from './schema'
import { FieldError } from './FieldError'
import { PrepareIssues } from './PrepareIssues'

interface StepDetailsProps {
  form: UseFormReturn<CommitmentLetterFormInput>
  data: CommitmentLetterPrepareResponse | undefined
  isLoading: boolean
}

export function StepDetails({ form, data, isLoading }: StepDetailsProps) {
  const {
    control,
    register,
    formState: { errors },
  } = form

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      {data && <PrepareIssues data={data} className="sm:col-span-2" />}

      <div className="flex flex-col gap-1.5">
        <Label>Confirmation due date</Label>
        <Controller
          control={control}
          name="dueDate"
          render={({ field }) => (
            <DatePickerCell
              value={field.value}
              onChange={field.onChange}
              minDate={startOfToday()}
              className="h-9 w-full"
            />
          )}
        />
        <p className="text-xs text-muted-foreground">
          Date the vendor must return the signed letter by.
        </p>
        <FieldError message={errors.dueDate?.message} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="cl-sample-count">Number of samples (per SKU)</Label>
        <Input
          id="cl-sample-count"
          type="number"
          min={0}
          className="h-9"
          {...register('sampleCount', { valueAsNumber: true })}
        />
        <FieldError message={errors.sampleCount?.message} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Sample date</Label>
        <Controller
          control={control}
          name="sampleDueDate"
          render={({ field }) => (
            <DatePickerCell
              value={field.value}
              onChange={field.onChange}
              minDate={startOfToday()}
              className="h-9 w-full"
            />
          )}
        />
        <p className="text-xs text-muted-foreground">
          POG samples are due to Model Store by this date.
        </p>
        <FieldError message={errors.sampleDueDate?.message} />
      </div>
    </div>
  )
}
